import { ReactNode, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFranchiseUser, useFranchiseBasePath } from "@/lib/franchise-auth";
import { useOrganization } from "@/hooks/use-organization";
import { setPortalChoice } from "@/hooks/use-dual-role";
import { useInactivityTimer } from "@/hooks/use-inactivity-timer";
import { InactivityWarning } from "@/components/InactivityWarning";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Building2,
  DollarSign,
  Trash2,
  Truck,
  Package,
  ClipboardList,
  CreditCard,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronRight,
  Store,
  Sun,
  Moon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import "@/franchise/franchise-theme.css";

type NavItem = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
};

const NAV_PATHS: NavItem[] = [
  { label: "Dashboard",      icon: LayoutDashboard, href: "/" },
  { label: "Operators",      icon: Building2,       href: "/orgs" },
  { label: "Spend",          icon: DollarSign,      href: "/spend" },
  { label: "Waste",          icon: Trash2,          href: "/waste" },
  { label: "Vendors",        icon: Truck,           href: "/vendors" },
  { label: "Products",       icon: Package,         href: "/products" },
  { label: "Catalog",        icon: ClipboardList,   href: "/catalog" },
  { label: "Billing",        icon: CreditCard,      href: "/billing" },
];

type FranchiseTheme = "dark" | "light";

export default function FranchiseLayout({ children }: { children: ReactNode }) {
  const { signOut, user } = useAuth();
  const { brand, role } = useFranchiseUser();
  const { allOrgs } = useOrganization();
  const basePath = useFranchiseBasePath();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { showWarning, dismissWarning } = useInactivityTimer(signOut);
  const [theme, setTheme] = useState<FranchiseTheme>(() =>
    (localStorage.getItem("franchise_theme") as FranchiseTheme) || "dark"
  );

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("franchise_theme", next);
      return next;
    });
  }, []);

  function navHref(path: string): string {
    if (basePath === "/") return path;
    return path === "/" ? basePath : `${basePath}${path}`;
  }

  const NAV_ITEMS = NAV_PATHS.map((item) => ({ ...item, href: navHref(item.href) }));

  function isActive(href: string) {
    const root = basePath === "/" ? "/" : basePath;
    if (href === root) return location.pathname === root || location.pathname === root + "/";
    return location.pathname.startsWith(href);
  }

  const currentLabel = NAV_ITEMS.find((i) => isActive(i.href))?.label ?? "Portal";

  return (
    <div data-theme={theme} className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: "var(--f-bg)" }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 backdrop-blur-sm lg:hidden"
          style={{ backgroundColor: "var(--f-overlay-bg)" }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col transition-transform lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{
          background: `linear-gradient(to bottom, var(--f-sidebar-from), var(--f-sidebar-via), var(--f-sidebar-to))`,
          borderRight: `1px solid var(--f-border)`,
        }}
      >
        {/* Branding */}
        <div className="flex h-16 items-center gap-3 px-5 relative overflow-hidden" style={{ borderBottom: `1px solid var(--f-border)` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 via-transparent to-indigo-500/5" />
          <div className="relative flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-400/20 to-indigo-600/10 p-2 ring-1 ring-violet-400/20">
            <Store className="h-5 w-5 text-violet-400 drop-shadow-[0_0_6px_rgba(167,139,250,0.4)]" />
          </div>
          <div className="relative flex-1 min-w-0">
            <p className="font-display text-sm font-bold tracking-tight truncate">
              <span className="bg-gradient-to-r from-violet-300 via-indigo-400 to-violet-500 bg-clip-text text-transparent f-brand-gradient">
                {brand.name}
              </span>
            </p>
            <p className="text-[11px] text-indigo-400/60">Franchise Portal</p>
          </div>
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
            style={{ color: "var(--f-text-muted)" }}
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 min-h-[44px] relative group",
                  active
                    ? "text-violet-300 shadow-lg shadow-violet-500/5 ring-1 ring-violet-400/15"
                    : ""
                )}
                style={{
                  backgroundColor: active ? "var(--f-active-bg)" : undefined,
                  color: active ? undefined : "var(--f-text-muted)",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = "var(--f-hover-bg)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = "";
                }}
              >
                <item.icon className={cn(
                  "h-4 w-4 transition-all duration-300 shrink-0",
                  active && "text-violet-400 drop-shadow-[0_0_4px_rgba(167,139,250,0.3)]"
                )}
                style={active ? undefined : { color: "var(--f-text-muted)" }} />
                {item.label}
                {active && <ChevronRight className="ml-auto h-3.5 w-3.5 text-violet-400/40" />}
              </Link>
            );
          })}
        </nav>

        {/* User card */}
        <div className="p-4 space-y-3" style={{ borderTop: `1px solid var(--f-border)` }}>
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-400/20 to-indigo-500/15 text-xs font-bold uppercase text-violet-300 ring-1 ring-violet-400/20 shrink-0">
              {user?.email?.[0] ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-xs font-medium" style={{ color: "var(--f-text-primary)" }}>{user?.email}</p>
              <p className="text-[11px] bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent font-medium capitalize">
                {role.replace("franchise_", "")}
              </p>
            </div>
          </div>
          {allOrgs.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 transition-all duration-200"
              style={{ color: "var(--f-text-muted)", border: "1px solid var(--f-border)" }}
              onClick={() => { setPortalChoice("operator"); navigate("/dashboard"); }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "var(--f-hover-bg)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ""; }}
            >
              <LayoutDashboard className="h-4 w-4" />
              Switch to Operator Dashboard
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 hover:bg-red-500/5 transition-all duration-200"
            style={{ color: "var(--f-text-muted)" }}
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header
          className="flex h-14 items-center gap-2 px-3 lg:h-16 lg:gap-4 lg:px-6 relative overflow-hidden"
          style={{ borderBottom: `1px solid var(--f-card-border)` }}
        >
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(to right, var(--f-header-bg), var(--f-header-via), var(--f-header-bg))` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/[0.03] via-transparent to-indigo-500/[0.03]" />
          <button
            className="relative lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center"
            style={{ color: "var(--f-text-muted)" }}
            onClick={() => setSidebarOpen(true)}
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <h2 className="relative font-display text-base font-semibold tracking-tight lg:text-lg" style={{ color: "var(--f-text-primary)" }}>
            {currentLabel}
          </h2>
          <div className="relative ml-auto flex items-center gap-2">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center justify-center h-8 w-8 rounded-lg transition-colors"
              style={{ backgroundColor: "var(--f-controls-bg)", color: "var(--f-text-muted)" }}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={signOut}
              className="flex items-center justify-center h-8 w-8 rounded-lg transition-colors"
              style={{ backgroundColor: "var(--f-controls-bg)", color: "var(--f-text-muted)" }}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" style={{ backgroundColor: "var(--f-bg)" }}>
          {children}
        </main>
      </div>
      <InactivityWarning open={showWarning} onStay={dismissWarning} onLogout={signOut} />
    </div>
  );
}
